import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import AndroidOutlinedIcon from "@material-ui/icons/AndroidOutlined";
import { toast } from "react-toastify";

import openSocket from "../../services/socket-io";
import {
  getPendingDraft,
  sendDraftReply,
  discardDraftReply,
} from "../../services/agentIa";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  banner: {
    margin: "8px 12px",
    padding: 12,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 8,
    background: theme.palette.type === "dark" ? "#1a2733" : "#eef6ff",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 6,
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  text: {
    whiteSpace: "pre-wrap",
    marginBottom: 10,
  },
  actions: {
    display: "flex",
    gap: 8,
  },
}));

// Sugerencia del Agente IA para el ticket real (modo "Solo sugerencia") —
// no se manda sola, el agente humano decide: enviar tal cual, corregir y
// enviar, o descartar. Vive aparte del hilo de mensajes a propósito, para
// no tocar la lógica de MessagesList.
const AgentIaSuggestionBanner = ({ ticketId }) => {
  const classes = useStyles();
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!ticketId) return;
    try {
      const data = await getPendingDraft(ticketId);
      setDraft(data || null);
    } catch (err) {
      // silencioso: no interrumpir el chat si esto falla
    }
  }, [ticketId]);

  useEffect(() => {
    setDraft(null);
    setEditing(false);
    fetchPending();
  }, [ticketId, fetchPending]);

  useEffect(() => {
    if (!ticketId) return undefined;
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("agentIaDraft", (data) => {
      if (data.action === "create" && String(data.draft.ticketId) === String(ticketId)) {
        setDraft(data.draft);
        setEditing(false);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId]);

  if (!draft) return null;

  const handleSend = async () => {
    setBusy(true);
    try {
      await sendDraftReply(draft.id, editing ? editValue : draft.suggestedAnswer);
      toast.success("Respuesta enviada.");
      setDraft(null);
      setEditing(false);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = async () => {
    setBusy(true);
    try {
      await discardDraftReply(draft.id);
      setDraft(null);
      setEditing(false);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleStartEdit = () => {
    setEditValue(draft.suggestedAnswer);
    setEditing(true);
  };

  return (
    <Paper className={classes.banner} elevation={0}>
      <div className={classes.header}>
        <AndroidOutlinedIcon fontSize="small" />
        <Typography variant="body2" component="span">
          Sugerencia del Agente IA
        </Typography>
      </div>

      {editing ? (
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          style={{ marginBottom: 10 }}
          disabled={busy}
        />
      ) : (
        <Typography variant="body2" className={classes.text}>
          {draft.suggestedAnswer}
        </Typography>
      )}

      <div className={classes.actions}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={busy}
          onClick={handleSend}
          endIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {editing ? "Enviar corrección" : "Enviar"}
        </Button>
        {!editing && (
          <Button size="small" disabled={busy} onClick={handleStartEdit}>
            Corregir
          </Button>
        )}
        {editing && (
          <Button size="small" disabled={busy} onClick={() => setEditing(false)}>
            Cancelar
          </Button>
        )}
        <Button size="small" color="secondary" disabled={busy} onClick={handleDiscard}>
          Descartar
        </Button>
      </div>
    </Paper>
  );
};

export default AgentIaSuggestionBanner;
